import { del, postMultiFile } from 'services/request';
import { IIllustration } from '../types/illustration';
import { IRefIds } from '../constants/refs';

type IIllustrationUpload = {
  status: number;
  error: string;
};

export default class IllustrationModel {
  /**
   * Upload PNG file using unified endpoint
   * @param ref - Entity type reference (e.g., IRefIds.vocs, IRefIds.vocabularyCategories)
   * @param id - Entity ID
   * @param file - PNG file to upload
   * @param isEnhanced - If true, uploads as enhanced (stage 2 - final), otherwise as original (stage 1)
   */
  async uploadPNGByRef(ref: number, id: number, file: File, isEnhanced: boolean = false) {
    const formData = [];
    formData.push({
      fileName: isEnhanced ? 'enhanced' : 'png',
      file: file,
    });

    const response = await postMultiFile<IIllustrationUpload>(`/png/${ref}/${id}`, formData);
    return response.data;
  }

  /**
   * Delete PNG file using unified endpoint
   * @param ref - Entity type reference (e.g., IRefIds.vocs, IRefIds.vocabularyCategories)
   * @param id - Entity ID
   */
  async deletePNGByRef(ref: number, id: number) {
    await del(`/png/${ref}/${id}`);
  }

  /**
   * Upload PNG file for vocabulary (PNG-only workflow)
   * @param data - Illustration data
   * @param file - PNG file to upload
   * @param isEnhanced - If true, uploads as enhanced (stage 2 - final), otherwise as original (stage 1)
   */
  async uploadPNG(data: IIllustration, file: File, isEnhanced: boolean = false) {
    return this.uploadPNGByRef(IRefIds.vocs, data.id, file, isEnhanced);
  }

  /**
   * Delete all PNG files for vocabulary (PNG-only workflow)
   */
  async deletePNG(data: IIllustration) {
    await this.deletePNGByRef(IRefIds.vocs, data.id);
  }

  /**
   * Delete only the enhanced/final PNG for vocabulary
   * Resets status to allow re-enhancement via AI
   */
  async deleteEnhancedPNG(data: IIllustration) {
    await this.deleteEnhancedPNGByRef(IRefIds.vocs, data.id);
  }

  /**
   * Delete only the enhanced/final PNG file using unified endpoint
   * @param ref - Entity type reference
   * @param id - Entity ID
   */
  async deleteEnhancedPNGByRef(ref: number, id: number) {
    await del(`/png/${ref}/${id}/enhanced`);
  }

  /**
   * Upload PNG file for vocabulary category (sub-categories only)
   */
  async uploadVocCategoryPNG(categoryId: number, filePNG: File) {
    return this.uploadPNGByRef(IRefIds.vocabularyCategories, categoryId, filePNG);
  }

  /**
   * Delete PNG file for vocabulary category
   */
  async deleteVocCategoryPNG(categoryId: number) {
    await this.deletePNGByRef(IRefIds.vocabularyCategories, categoryId);
  }
}
